import { Timestamp } from "@bufbuild/protobuf";
import { CE_ChannelType } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/connector_info_pb";
import { ConversationsChatMessage } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/conversations_pb";
import type { DocumentInfo as GrpcDocumentInfo } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/models_pb";

import type {
  Channel,
  ChannelType,
  ConversationMessage,
  ConversationMessageJsonContent,
  DocumentInfo,
  MessageStatus,
} from "@mirage/service-conversation/types";

/**
 * Safely converts a timestamp from various possible formats to a Date object
 * @param timestamp The timestamp to convert
 * @returns A Date object or undefined if conversion is not possible
 */
const safelyConvertTimestamp = (timestamp?: Timestamp): Date | undefined => {
  if (!timestamp) return undefined;

  if (
    typeof timestamp === "object" &&
    "seconds" in timestamp &&
    "nanos" in timestamp &&
    typeof timestamp.seconds === "bigint"
  ) {
    // Convert BigInt to number (safe as timestamps are within Number.MAX_SAFE_INTEGER)
    const milliseconds =
      Number(timestamp.seconds) * 1000 +
      Math.floor(Number(timestamp.nanos) / 1e6);
    return new Date(milliseconds);
  }

  return undefined;
};

/**
 * Maps a CE_ChannelType to our internal ChannelType
 */
function mapChannelType(type: CE_ChannelType): ChannelType {
  switch (type) {
    case CE_ChannelType.CHANNEL_TYPE_PUBLIC:
      return { ".tag": "channel_type_public" };
    case CE_ChannelType.CHANNEL_TYPE_PRIVATE:
      return { ".tag": "channel_type_private" };
    case CE_ChannelType.CHANNEL_TYPE_DM:
      return { ".tag": "channel_type_dm" };
    case CE_ChannelType.CHANNEL_TYPE_UNSET:
    default:
      return { ".tag": "channel_type_unset" };
  }
}

/**
 * Creates a default Channel when channel is undefined
 */
function createDefaultChannel(): Channel {
  return {
    id: "",
    name: "",
    url: "",
    created_at_ms: 0,
    type: { ".tag": "channel_type_unset" },
  };
}

/**
 * Transforms a document from the gRPC API to our internal DocumentInfo type
 */
export function transformDocument(doc: GrpcDocumentInfo): DocumentInfo {
  // Convert channel if it exists, otherwise use default
  const channel: Channel = doc.channel
    ? {
        id: doc.channel.id,
        name: doc.channel.name,
        url: doc.channel.url,
        created_at_ms: Number(doc.channel.createdAtMs),
        members: doc.channel.members.map((member) => ({
          display_name: member.displayName,
          email: member.email,
          profile_image_url: member.profileImageUrl,
          alias_handle: member.aliasHandle,
          profile_link: member.profileLink,
        })),
        type: mapChannelType(doc.channel.type),
      }
    : createDefaultChannel();

  return {
    url: doc.url,
    title: doc.title,
    lastUpdatedMs: Number(doc.lastUpdatedMs),
    brandedType: doc.brandedType,
    body: doc.body,
    ownerId: doc.ownerId,
    thirdPartyId: doc.thirdPartyId,
    relevanceScore: doc.relevanceScore,
    uuid: doc.uuid,
    index: doc.index,
    connectorInfo: doc.connectorInfo,
    fileTypeInfo: doc.fileTypeInfo,
    channel,
    citationType: doc.citationType || "",
    retrievalSource: doc.retrievalSource || "",
    transient: doc.transient || false,
  };
}

/**
 * Transforms an internal DocumentInfo to gRPC DocumentInfo format
 */
function transformDocumentToGrpc(doc: DocumentInfo) {
  const grpcChannel = doc.channel
    ? {
        id: doc.channel.id,
        name: doc.channel.name,
        url: doc.channel.url,
        createdAtMs: BigInt(doc.channel.created_at_ms),
        members: (doc.channel.members || []).map((member) => ({
          displayName: member.display_name,
          email: member.email,
          profileImageUrl: member.profile_image_url,
          aliasHandle: member.alias_handle,
          profileLink: member.profile_link,
        })),
        type: mapToGrpcChannelType(doc.channel.type),
      }
    : undefined;
  return {
    url: doc.url,
    title: doc.title,
    lastUpdatedMs: BigInt(doc.lastUpdatedMs),
    brandedType: doc.brandedType,
    body: doc.body,
    ownerId: doc.ownerId,
    thirdPartyId: doc.thirdPartyId,
    relevanceScore: doc.relevanceScore,
    uuid: doc.uuid,
    index: doc.index,
    connectorInfo: doc.connectorInfo,
    fileTypeInfo: doc.fileTypeInfo,
    channel: grpcChannel,
  };
}

/**
 * Converts a ConversationsChatMessage from the gRPC API to our internal ConversationMessage type
 * @param message The ConversationsChatMessage to convert
 * @param status The status to assign to the converted message
 * @returns A ConversationMessage
 */
export const convertToConversationMessage = (
  message: ConversationsChatMessage,
  status: MessageStatus,
): ConversationMessage => {
  // Safely convert timestamp if it exists
  const timestamp = safelyConvertTimestamp(message.timestamp);
  let jsonContent;
  switch (message.messageType.case) {
    case "systemMessage":
      return {
        id: message.id,
        timestamp,
        messageType: message.messageType,
        status,
      };
    case "assistantMessage":
      // Clean up the transport variable from the updated type and set it
      // on the message
      if ("_jsonContent" in message) {
        jsonContent = {
          ...(message._jsonContent as ConversationMessageJsonContent),
        };
      }
      return {
        id: message.id,
        timestamp,
        messageType: {
          case: "assistantMessage",
          value: {
            text: message.messageType.value.text,
            isCached: message.messageType.value.isCached,
            cited: message.messageType.value.cited.map(transformDocument),
            context: message.messageType.value.context.map(transformDocument),
            agentId: message.messageType.value.agentId,
            format: message.messageType.value.format,
          },
        },
        status,
        jsonContent,
      };
    case "userMessage":
      return {
        id: message.id,
        messageType: {
          case: "userMessage",
          value: {
            ...message.messageType.value,
            agentPayload: message.messageType.value.agentPayload,
            llmIgnore: message.messageType.value.llmIgnore,
            attachments:
              message.messageType.value.attachments.map(transformDocument),
          },
        },
        timestamp,
        status,
      };
    default:
      return {
        id: message.id,
        timestamp: timestamp,
        messageType: { case: undefined },
        status: status,
      };
  }
};

/**
 * Maps our internal ChannelType to CE_ChannelType
 */
export function mapToGrpcChannelType(type: ChannelType): CE_ChannelType {
  if (type[".tag"] === "channel_type_public") {
    return CE_ChannelType.CHANNEL_TYPE_PUBLIC;
  } else if (type[".tag"] === "channel_type_private") {
    return CE_ChannelType.CHANNEL_TYPE_PRIVATE;
  } else if (type[".tag"] === "channel_type_dm") {
    return CE_ChannelType.CHANNEL_TYPE_DM;
  } else {
    return CE_ChannelType.CHANNEL_TYPE_UNSET;
  }
}

/**
 * Converts our internal ConversationMessage type to a ConversationsChatMessage for the gRPC API
 * @param message The ConversationMessage to convert
 * @returns A ConversationsChatMessage
 */
export const convertToConversationsChatMessage = (
  message: ConversationMessage,
): ConversationsChatMessage => {
  const grpcTimestamp = message.timestamp
    ? new Timestamp({
        seconds: BigInt(Math.floor(message.timestamp.getTime() / 1000)),
        nanos: (message.timestamp.getTime() % 1000) * 1e6,
      })
    : undefined;
  switch (message.messageType.case) {
    case "systemMessage":
      return new ConversationsChatMessage({
        id: message.id,
        timestamp: grpcTimestamp,
        messageType: {
          case: message.messageType.case,
          value: message.messageType.value,
        },
      });
    case "assistantMessage":
      return new ConversationsChatMessage({
        id: message.id,
        timestamp: grpcTimestamp,
        messageType: {
          case: message.messageType.case,
          value: {
            text: message.messageType.value.text,
            isCached: message.messageType.value.isCached,
            cited: message.messageType.value.cited.map(transformDocumentToGrpc),
            context:
              message.messageType.value.context?.map(transformDocumentToGrpc) ||
              [],
            agentId: message.messageType.value.agentId,
            format: message.messageType.value.format,
          },
        },
      });
    case "userMessage":
      return new ConversationsChatMessage({
        id: message.id,
        timestamp: grpcTimestamp,
        messageType: {
          case: message.messageType.case,
          value: {
            text: message.messageType.value.text,
            rephrased: message.messageType.value.rephrased,
            llmIgnore: message.messageType.value.llmIgnore,
            attachments: message.messageType.value.attachments.map(
              transformDocumentToGrpc,
            ),
            agentPayload: message.messageType.value.agentPayload,
          },
        },
      });
    default:
      return new ConversationsChatMessage({
        id: message.id,
        timestamp: grpcTimestamp,
      });
  }
};
